import React, { useMemo, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Wizard,
  LayoutContainer,
  Layout,
  StepContainer,
} from '@bingads-webui-react/wizard';
import {
  SaveBlocker,
  DefaultTemplate,
  CancelWizardDialog,
} from '@bingads-webui-react/wizard-ui-library';
import { FluentSwitcherContext } from '@bingads-webui-react/fluent-version-switcher';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { WizardPerfMarker } from '@bingads-webui-campaign/wizard-performance-marker';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { PerformanceMarkerProvider } from '@bingads-webui-campaign-react/performance-marker-provider';
import { setClarityTag } from '@bingads-webui-universal/clarity';

export const UnifiedAppWizard = ({
  action, storeType, wizardConfig, perfMarker, children,
}) => {
  const instrumentation = useContext(InstrumentationContext);

  const wizardName = useMemo(() => `UnifiedAppCampaign${action}-${storeType}`, [action, storeType]);

  const wizardPerfMarker = useMemo(() => (
    perfMarker.createChild(wizardName)
  ), [perfMarker, wizardName]);

  useEffect(() => {
    setClarityTag({ [`UnifiedAppCampaign${action}`]: storeType });
  }, [action, storeType]);

  return (
    <FluentSwitcherContext.Provider value>
      <PerformanceMarkerProvider
        name={wizardName}
        instrumentation={instrumentation}
        immediateDone={false}
      >
        <Scenario name={wizardName}>
          <Activity name="DefaultWizardActivity">
            <Wizard config={wizardConfig}>
              <WizardPerfMarker wizardName={wizardName} wizardMarker={wizardPerfMarker} >
                <LayoutContainer template={DefaultTemplate}>
                  <Layout type="StepContainer" component={StepContainer} />
                  <Layout type="Blocker" component={SaveBlocker} />
                  <Layout type="CancelDialog" component={CancelWizardDialog} />
                  {children}
                </LayoutContainer>
              </WizardPerfMarker>
            </Wizard>
          </Activity>
        </Scenario>
      </PerformanceMarkerProvider>
    </FluentSwitcherContext.Provider>
  );
};

UnifiedAppWizard.propTypes = {
  perfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  action: PropTypes.string.isRequired,
  storeType: PropTypes.string.isRequired,
  wizardConfig: PropTypes.shape({}).isRequired,
  children: PropTypes.node,
};

UnifiedAppWizard.defaultProps = {
  children: null,
};
